import { Capacitor } from "@capacitor/core";
import { Box, Container } from "@mui/material";
import { DateTime } from "luxon";
import { type FC, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { CustomCalendar } from "../../../components/CustomCalendar/CustomCalendar";
import { MobileNursesList } from "../../../components/staffing/mobile/NursesList/MobileNursesList";
import { HolidayDayBanner } from "../../../components/team/Holidays/schedule/HolidayDayBanner";
import { ScheduleHolidaysProvider } from "../../../components/team/Holidays/schedule/ScheduleHolidaysProvider";
import { useAuth } from "../../../contexts/AuthProvider";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import type { NurseInShift } from "../../../utils/models";
import { MobilePromotionBanner } from "../MobilePromotionBanner";

export const DayScheduleMobile: FC = () => {
	const { date: dateString } = useParams();
	const { hospitalData, chosenDepartment, transformedShifts, refreshDepartmentData, refreshNursesData } = useHospitalData();
	const { userInfo } = useAuth();
	const [selectedDate, setSelectedDate] = useState<DateTime>(DateTime.fromISO(dateString as string));
	const hasFetchedRef = useRef(false);

	useEffect(() => {
		if (!hasFetchedRef.current) {
			hasFetchedRef.current = true;
			init();
		}
	}, []);

	const init = async (): Promise<void> => {
		await Promise.all([refreshDepartmentData(chosenDepartment?.department_id, true, selectedDate), refreshNursesData(chosenDepartment?.department_id)]);
	};

	const weekIndex = useMemo<number>(() => {
		const formattedDate: string = selectedDate.toFormat("yyyy-MM-dd");

		return hospitalData.weeks.findIndex((week) => week.weeklyShifts[0].days.some((day) => day.date.toFormat("yyyy-MM-dd") === formattedDate));
	}, [selectedDate, hospitalData.weeks]);

	const nurses = useMemo<NurseInShift[]>(() => {
		const allNurses: NurseInShift[] = [];
		const weeklyShifts = hospitalData.weeks[weekIndex]?.weeklyShifts || [];

		weeklyShifts.forEach((shift) => {
			if (shift.days?.[selectedDate.weekday % 7]?.nurses) {
				shift.days[selectedDate.weekday % 7].nurses.forEach((nurse: NurseInShift) => {
					if (!nurse.isPendingToDelete) {
						allNurses.push(nurse);
					}
				});
			}
		});

		const nurseId = userInfo.nurses_info?.find((nurseInfo) => nurseInfo.departments.includes(chosenDepartment.department_id))?.nurse.id;

		return allNurses.sort((a, b) => {
			if (nurseId !== undefined) {
				if (a.nurseId === nurseId && b.nurseId !== nurseId) return -1;
				if (a.nurseId !== nurseId && b.nurseId === nurseId) return 1;
			}
			if (a.charge && !b.charge) return -1;
			if (!a.charge && b.charge) return 1;

			const timeCompare: number = a.startTime.valueOf() - b.startTime.valueOf();
			if (timeCompare !== 0) return timeCompare;

			const roleCompare: number = b.role?.toLowerCase().localeCompare(a.role?.toLowerCase());
			if (roleCompare !== 0) return roleCompare;

			return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
		});
	}, [hospitalData.weeks, selectedDate, weekIndex, userInfo, chosenDepartment]);

	return (
		<ScheduleHolidaysProvider>
			<Box
				data-testid="day-schedule-mobile-root"
				sx={{
					flexGrow: 1,
					height: "calc(100vh - 50px)",
					overflow: "auto",
					bgcolor: "#F8F9FC",
					position: "absolute",
					top: "50px",
					left: 0,
					right: 0,
					bottom: 0,
					p: 0,
					m: 0,
					scrollBehavior: "smooth",
					overscrollBehavior: "contain",
				}}
			>
				<Container maxWidth="lg" sx={{ height: "100%", display: "flex", flexDirection: "column", p: 0, m: 0, overflowX: "hidden" }}>
					<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5, width: "100%", mx: "auto", pb: 10 }}>
						<Box sx={{ position: "sticky", top: 0, width: "100%", zIndex: 5, bgcolor: "#F8F9FC" }}>
							<CustomCalendar shifts={transformedShifts} value={selectedDate} onChange={setSelectedDate} showOneWeek={true} showTitle={true} />
						</Box>
						{/* The mobile Team page has no Holidays tab, so the banner has no link here. */}
						<HolidayDayBanner date={selectedDate} showViewHolidays={false} sx={{ width: "100%", px: 2 }} dataTestId="mobile-day-holiday-banner" />
						<Box sx={{ width: "100%" }}>
							<MobileNursesList nurses={nurses} date={selectedDate} weekIndex={weekIndex} />
						</Box>
					</Box>
				</Container>
			</Box>

			{!Capacitor.isNativePlatform() && <MobilePromotionBanner />}
		</ScheduleHolidaysProvider>
	);
};
